import { Component, EventEmitter, Input, OnInit, Output, OnChanges, SimpleChanges } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { IZonaCiudad } from './interfaces/zona-ciudad.interface';
import { ZonaCiudadService } from './services/zona-ciudad.service';
import { MatDialog } from '@angular/material/dialog';
import { AbcEmpresasCustomizablesComponent } from '@p-parametria/mantenimiento/abc-empresas/components/abc-empresas-customizables/abc-empresas-customizables.component';

@Component({
  selector: 'app-zona-ciudad-form-ciudad',
  templateUrl: './zona-ciudad-form-ciudad.component.html',
  providers: [MessageService]
})
export class ZonaCiudadFormCiudadComponent implements OnInit, OnChanges {
  @Input() ciudad: IZonaCiudad | null = null;
  @Input() zona: string | null = null;
  @Output() onSave = new EventEmitter<{ success: boolean; message: string }>();
  @Output() onHide = new EventEmitter<void>();

  form!: FormGroup;
  loading: boolean = false;

  constructor(
    private fb: FormBuilder,
    private messageService: MessageService,
    private zonaCiudadService: ZonaCiudadService,
    private dialog: MatDialog
  ) {}

  ngOnInit(): void {
    this.resetForm();
    if (this.ciudad) {
      this.loadCiudad();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.resetForm();
    if (this.ciudad) {
      this.loadCiudad();
    } else {
      // Si es modo creación, limpiar y habilitar campos
      this.form.get('claveCd')?.reset();
      this.form.get('claveCd')?.enable();
      this.form.get('ciu_mun_del')?.reset();
      this.form.get('ciu_mun_del')?.enable();
      this.form.patchValue({ claveZona: this.zona });
    }
  }

  resetForm(): void {
    if (this.form) {
      this.form.reset();
    }
    this.form = this.fb.group({
      claveZona: [{ value: this.zona, disabled: true }],
      claveCd: ['', [Validators.required]],
      ciu_mun_del: ['', [Validators.required]]
    });
  }

  loadCiudad(): void {
    if (this.ciudad && this.zona && this.ciudad['claveCd']) {
      this.loading = true;
      this.zonaCiudadService.getCiudadForEdit(this.zona, this.ciudad['claveCd']).subscribe({
        next: (response) => {
          if (response.success && response.data.length > 0) {
            const ciudad = response.data[0];
            this.form.patchValue({
              claveZona: ciudad.zona,
              claveCd: ciudad.claveCd,
              ciu_mun_del: ciudad.ciu_mun_del
            });
            this.form.get('claveCd')?.disable();
          }
        },
        error: () => {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'No se pudo cargar la ciudad'
          });
        },
        complete: () => {
          this.loading = false;
        }
      });
    } else {
      this.form.patchValue({ claveZona: this.zona });
      this.form.get('claveCd')?.enable();
    }
  }

  create(): void {
    if (this.form.valid && this.zona) {
      this.loading = true;
      const formValue = this.form.getRawValue();
      const payload = {
        zona: this.zona,
        claveCd: formValue.claveCd,
        ciu_mun_del: formValue.ciu_mun_del
      };
      this.zonaCiudadService.createCiudad(payload).subscribe({
        next: (response) => {
          if (response.success) {
            this.onSave.emit({ success: true, message: 'Ciudad creada correctamente' });
          } else {
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: 'No se pudo crear la ciudad'
            });
          }
        },
        error: (error) => {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Error al crear la ciudad'
          });
        },
        complete: () => {
          this.loading = false;
        }
      });
    } else {
      this.markFormGroupTouched(this.form);
    }
  }

  update(): void {
    if (this.form.valid && this.zona && this.ciudad) {
      this.loading = true;
      const formValue = this.form.getRawValue();
      const payload = {
        zona: this.zona,
        claveCd: this.ciudad['claveCd'],
        ciu_mun_del: formValue.ciu_mun_del
      };
      this.zonaCiudadService.updateCiudad(payload).subscribe({
        next: (response) => {
          if (response.success) {
            this.onSave.emit({ success: true, message: 'Ciudad actualizada correctamente' });
          } else {
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: 'No se pudo actualizar la ciudad'
            });
          }
        },
        error: (error) => {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Error al actualizar la ciudad'
          });
        },
        complete: () => {
          this.loading = false;
        }
      });
    } else {
      this.markFormGroupTouched(this.form);
    }
  }

  private markFormGroupTouched(formGroup: FormGroup): void {
    Object.values(formGroup.controls).forEach(control => {
      control.markAsTouched();
      if (control instanceof FormGroup) {
        this.markFormGroupTouched(control);
      }
    });
  }

  openCustomizables(): void {
    this.dialog.open(AbcEmpresasCustomizablesComponent, {
      width: '1000px',
      height: '1000px',
      disableClose: true,
      panelClass: ['custom-dialog-zindex'],
      hasBackdrop: true,
      autoFocus: false,
      data: {
        sTabla: 'zonaciudades',
        sClave: this.ciudad?.['claveCd'] || '',
        sNombre: this.form.get('ciu_mun_del')?.value,
        sTipo: 'Opcion'
      }
    });
  }

  onDialogClosed(): void {
    this.onHide.emit();
  }
} 